/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'underscore',
	'../../../lib/@waca/upgrades/UpgradeBase',
	'dashboard-analytics/dataSources/services/DataSourcesService'
], (_, UpgradeBase, DataSourcesService) => {

	/**
	 * Upgrade the aggragation type of categorical data item for color slot.
	 **/
	class DataItemAggregationTypeUpgrade extends UpgradeBase {

		constructor() {
			super();
			this.VERSION = 1018;
		}

		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		up(spec) {
			if (!spec || !spec.widgets || _.isEmpty(spec.widgets)) {
				return Promise.resolve(spec);
			}
			this.spec = spec;

			const dataSourcesClass  = this._DataSourcesServices || DataSourcesService;
			this.dataSourcesService =  new dataSourcesClass({
				features:{
					API: this.data.dashboardApi,
					Logger: this.data.logger
				}
			});
			this.dataSourcesService.initializeSourcesCollection(this.spec);
			return this._upgradeAggregationType(spec);
		}

		// Downgrades are not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		/**
		* In R stream, we do not support categorical data in color slots to be summarized, when user drag a categorical type data into the color slots, right click to bring up the contextual toolbar does not have the option for summarize, in endor, we enabled color slots for any type of data, by default the summarize type of categorical data will be set to none.
		* But there is a back door for summazie color slot categorical data in r stream, by summaized the data item in a ordinal slot and then drag it to the color slots, now in the spec, the data item in the color slots is summarized with 'count' or 'distinct count'
		* This would cause upgrade issue since those slots will be treated as ordinal type of data in endor
		* the fix is to go through the spec to find categorical type data in color slots and update its aggregate type to 'none'
		*
		* @param {object} spec - spec to perform upgrade on
		*
		* @return {Promise} Promise to be resolved when upgrade performed
		*/
		_upgradeAggregationType(spec){
			var promises = [];
			_.each(spec.widgets, widget => {
				let dataViews = widget.data && widget.data.dataViews;
				let slots = widget.slotmapping && widget.slotmapping.slots;
				let isLiveWidgetType = widget.type === 'live';
				let modelRef = dataViews && dataViews[0] ? dataViews[0].modelRef : null;
				if (modelRef && dataViews && slots && isLiveWidgetType) {
					let colorSlotsDataItems = [];
					_.each(slots, slot => {
						// get data items in the color slots
						if (slot.name === 'color' && slot.dataItems[0] !== '_multiMeasuresSeries') {
							colorSlotsDataItems = slot.dataItems;
						}
					});
					// if the color slots have data items start upgrade
					if (colorSlotsDataItems.length > 0) {
						// get data module with dataset service
						let promise = this._getModule(modelRef).then(function(module){
							_.each(dataViews, dataView => {
								let dataItems = dataView.dataItems;
								if (dataItems){
									_.each(dataItems, dataItem =>{
										// get metadata with column id
										const mdc = module.getMetadataColumn(dataItem.itemId);
										let dataType = mdc && mdc.getType();
										// if the data type is categorical and it is assigned to the color slots, update aggregate type to 'none'
										if (_.contains(colorSlotsDataItems,dataItem.id) && dataType === 'attribute'){
											dataItem.aggregate = 'none';
										}
									});
								}
							});
						});
						promises.push(promise);
					}
				}
			});
			return Promise.all(promises).then(function(){ return spec; });
		}

		_getModule(modelRef) {
			return this.dataSourcesService.getModule(modelRef);
		}

	}

	return new DataItemAggregationTypeUpgrade();
});
